import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { HarnessWordmark } from "../components/HarnessWordmark";
import { t } from "../i18n/en";
import { appQueryKeys, type ChartEditDraft } from "../services/app-query-state";
import {
  clearChartLibrarySession,
  completeGoogleSignIn,
  getChartLibrarySession,
  getPrimaryChartForEmail,
  requestLibrarySignIn,
  saveChartToLibrary,
} from "../services/chart-library";
import { clearPendingFriendInvite, rememberPendingFriendInvite } from "../services/friend-invite";
import {
  acceptFriendInvite,
  type ClaimChartPreview,
  FriendsRequestError,
  previewFriendInvite,
} from "../services/friends-service";
import { pageHead, pageTitle } from "../utils/page-title";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize(options: {
            client_id: string;
            callback: (response: { credential?: string }) => void;
            itp_support?: boolean;
            ux_mode?: "popup" | "redirect";
            login_uri?: string;
          }): void;
          renderButton(
            parent: HTMLElement,
            options: Record<string, string | number | boolean>,
          ): void;
        };
      };
    };
  }
}

const asteriskMarks = [0, 1, 2, 3, 4];
type InviteKind = "connection" | "claim-chart";

function inviteTitle(senderName: string, kind: InviteKind | null) {
  if (senderName === "A friend") return "Accept an Invitation";
  return kind === "connection"
    ? `${senderName} invited you to connect on Harness`
    : `${senderName} invited you to see a Human Design reading`;
}

const getInvitePageMetadata = createServerFn({ method: "GET" })
  .validator((token: string) => (/^[A-Za-z0-9_-]{20,128}$/.test(token) ? token : ""))
  .handler(async ({ data }) => {
    if (!data) return null;
    try {
      const { readInvitePageMetadata } = await import("../server/invite-page-metadata");
      return await readInvitePageMetadata(data);
    } catch {
      return null;
    }
  });

function ChartreuseAsteriskCluster({ className }: { className: string }) {
  return (
    <span className={`reading-card-asterisk-cluster ${className}`} aria-hidden="true">
      {asteriskMarks.map((mark) => (
        <span key={mark}>
          <svg viewBox="0 0 20 20" aria-hidden="true">
            <path d="M10 2.5 V17.5 M3.4 6.2 L16.6 13.8 M3.4 13.8 L16.6 6.2" />
          </svg>
        </span>
      ))}
    </span>
  );
}

export const Route = createFileRoute("/accept")({
  component: AcceptChartPage,
  validateSearch: (search) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
  loaderDeps: ({ search }) => ({ token: search.token }),
  loader: async ({ deps }) => {
    if (!deps.token) return null;
    const metadata = await getInvitePageMetadata({ data: deps.token });
    return metadata ? { ...metadata, token: deps.token } : null;
  },
  head: ({ loaderData }) => {
    const title = inviteTitle(loaderData?.senderName || "A friend", loaderData?.kind || null);
    const description =
      loaderData?.kind === "connection"
        ? `${loaderData.senderName} invited you to connect on Harness.`
        : loaderData
          ? `${loaderData.senderName} invited you to see a Human Design reading on Harness.`
          : "Accept an invitation on Harness.";
    return pageHead(title, {
      description,
      canonicalPath: loaderData?.token
        ? `/accept?token=${encodeURIComponent(loaderData.token)}`
        : "/accept",
      robots: "noindex, nofollow",
    });
  },
});

function AcceptChartPage() {
  const inviteMetadata = Route.useLoaderData();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [token] = useState(
    () =>
      (typeof window === "undefined"
        ? ""
        : new URLSearchParams(window.location.search).get("token")) || "",
  );
  const [senderName, setSenderName] = useState(inviteMetadata?.senderName || "A friend");
  const [inviteKind, setInviteKind] = useState<InviteKind | null>(inviteMetadata?.kind || null);
  const [claimPreview, setClaimPreview] = useState<ClaimChartPreview | null>(null);
  const [ownerChartStatus, setOwnerChartStatus] = useState<
    "checking" | "available" | "missing" | "unavailable"
  >("checking");
  const [existingOwnerChartName, setExistingOwnerChartName] = useState("");
  const [email, setEmail] = useState("");
  const [showEmail, setShowEmail] = useState(false);
  const [status, setStatus] = useState("");
  const [isAccepted, setIsAccepted] = useState(false);
  const [devMagicLink, setDevMagicLink] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const isChartClaimHeading = !isAccepted && inviteKind !== "connection";
  const hasExistingOwnerChart = ownerChartStatus === "available";
  const googleButtonRef = useRef<HTMLDivElement | null>(null);
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

  useEffect(() => {
    if (!token) return;
    document.title = pageTitle(inviteTitle(senderName, inviteKind));
  }, [inviteKind, senderName, token]);

  useEffect(() => {
    if (!inviteMetadata) return;
    setSenderName(inviteMetadata.senderName);
    setInviteKind(inviteMetadata.kind);
  }, [inviteMetadata]);

  useEffect(() => {
    if (!token) return;
    queryClient.setQueryData(appQueryKeys.pendingChartClaim(), token);
    rememberPendingFriendInvite(token);
    void previewFriendInvite(token)
      .then((preview) => {
        setSenderName(firstName(preview.inviterName));
        setInviteKind(preview.kind);
        setClaimPreview(preview.kind === "claim-chart" ? preview.chart || null : null);
      })
      .catch((previewError) =>
        setError(
          previewError instanceof Error
            ? previewError.message
            : t("ui.copy_this_chart_invitation_could_not_be_o_20a8d479"),
        ),
      );
  }, [queryClient, token]);

  const acceptConnection = useCallback(async () => {
    if (!token) {
      setError(t("ui.copy_this_invitation_is_missing_its_link_2c6d0416"));
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      await acceptFriendInvite(token);
      queryClient.removeQueries({ queryKey: appQueryKeys.pendingChartClaim() });
      window.localStorage.removeItem("human-design.pending-friend-invite");
      setIsAccepted(true);
      setStatus(t("ui.copy_you_are_now_connected_with_value_a02b98d4", { senderName: senderName }));
    } catch (acceptError) {
      if (
        acceptError instanceof FriendsRequestError &&
        acceptError.status === 400 &&
        acceptError.message === "Create your primary chart before accepting this invitation."
      ) {
        setOwnerChartStatus("missing");
        setError("");
        return;
      }
      setError(
        acceptError instanceof Error
          ? acceptError.message
          : t("ui.copy_this_invitation_could_not_be_accepte_c57a7d59"),
      );
    } finally {
      setIsLoading(false);
    }
  }, [queryClient, senderName, token]);

  const acceptWithoutClaimingChart = useCallback(async () => {
    if (!token) {
      setError(t("ui.copy_this_invitation_is_missing_its_link_2c6d0416"));
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      await acceptFriendInvite(token);
      queryClient.removeQueries({ queryKey: appQueryKeys.pendingChartClaim() });
      window.localStorage.removeItem("human-design.pending-friend-invite");
      setIsAccepted(true);
      setStatus(t("ui.copy_the_invitation_was_accepted_your_exi_7ba7f5b8"));
    } catch (acceptError) {
      setError(
        acceptError instanceof Error
          ? acceptError.message
          : t("ui.copy_this_invitation_could_not_be_accepte_c57a7d59"),
      );
    } finally {
      setIsLoading(false);
    }
  }, [queryClient, token]);

  const saveInvitedChartAsShared = useCallback(async () => {
    const session = getChartLibrarySession();
    if (!token || !session || !claimPreview) {
      setError(t("ui.copy_sign_in_to_save_this_shared_chart_507d9bd8"));
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      const dashboard = await acceptFriendInvite(token);
      const friend = dashboard.friends.find(
        (candidate) => candidate.chart?.chartName === claimPreview.chartName,
      );
      await saveChartToLibrary(
        {
          ...claimPreview,
          isOwnerChart: false,
          ...(friend ? { linkedFriendshipId: friend.friendshipId } : {}),
        },
        session.email,
      );
      queryClient.removeQueries({ queryKey: appQueryKeys.pendingChartClaim() });
      window.localStorage.removeItem("human-design.pending-friend-invite");
      setIsAccepted(true);
      setStatus(t("ui.copy_the_invitation_was_accepted_and_the_06162be4"));
    } catch (acceptError) {
      setError(
        acceptError instanceof Error
          ? acceptError.message
          : t("ui.copy_the_shared_chart_could_not_be_saved_42b2878b"),
      );
    } finally {
      setIsLoading(false);
    }
  }, [claimPreview, queryClient, token]);

  const signOutToAcceptAsAnotherUser = useCallback(() => {
    clearChartLibrarySession();
    window.location.assign(`/accept?token=${encodeURIComponent(token)}`);
  }, [token]);

  useEffect(() => {
    const session = getChartLibrarySession();
    if (!session || !token || inviteKind !== "claim-chart") return;
    let cancelled = false;
    setOwnerChartStatus("checking");
    setExistingOwnerChartName("");

    void (async () => {
      try {
        const ownerChart = await getPrimaryChartForEmail(session.email);
        if (cancelled) return;
        setOwnerChartStatus(ownerChart ? "available" : "missing");
        setExistingOwnerChartName(ownerChart?.chartName || "");
      } catch {
        if (!cancelled) setOwnerChartStatus("unavailable");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [inviteKind, token]);

  useEffect(() => {
    const activeGoogleClientId = [
      "harnessmyhd.com",
      "staging.harnessmyhd.com",
      "harness-staging.netlify.app",
      "localhost",
      "127.0.0.1",
    ].includes(window.location.hostname)
      ? (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined)
      : undefined;
    if (!activeGoogleClientId || !googleButtonRef.current || getChartLibrarySession()) return;
    const render = () => {
      if (!window.google || !googleButtonRef.current) return;
      googleButtonRef.current.replaceChildren();
      window.google.accounts.id.initialize({
        client_id: activeGoogleClientId,
        ux_mode: "popup",
        callback: (response) => {
          if (!response.credential)
            return setError(t("ui.copy_google_did_not_return_a_sign_in_cred_1bc54887"));
          setIsLoading(true);
          void completeGoogleSignIn(response.credential)
            .then(() => window.location.assign(`/accept?token=${encodeURIComponent(token)}`))
            .catch((signInError) =>
              setError(
                signInError instanceof Error
                  ? signInError.message
                  : t("ui.copy_google_sign_in_failed_bbf9b44c"),
              ),
            )
            .finally(() => setIsLoading(false));
        },
      });
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        type: "standard",
        theme: "outline",
        size: "large",
        shape: "pill",
        text: "continue_with",
        width: Math.min(512, googleButtonRef.current.clientWidth),
      });
    };
    const script = document.querySelector<HTMLScriptElement>(
      'script[src="https://accounts.google.com/gsi/client"]',
    );
    if (script) {
      if (window.google) render();
      else script.addEventListener("load", render, { once: true });
      return;
    }
    const newScript = document.createElement("script");
    newScript.src = "https://accounts.google.com/gsi/client";
    newScript.async = true;
    newScript.addEventListener("load", render, { once: true });
    document.head.appendChild(newScript);
  }, [token]);

  const reviewChart = useCallback(() => {
    if (!claimPreview) return;
    const session = getChartLibrarySession();
    if (!session) {
      setError(t("ui.copy_sign_in_to_review_this_chart_379fe717"));
      return;
    }
    queryClient.setQueryData<ChartEditDraft>(appQueryKeys.editDraft(), {
      email: session.email,
      name: claimPreview.chartName,
      date: claimPreview.birthDate,
      time: claimPreview.birthTime,
      city: claimPreview.location,
      phoneNumber: claimPreview.phoneNumber || "",
      chartOwner: "self",
      claimInviteToken: token,
    });
    void navigate({ to: "/wizard" });
  }, [claimPreview, navigate, queryClient, token]);

  const createPrimaryChart = useCallback(() => {
    queryClient.setQueryData(appQueryKeys.newChart(), true);
    void navigate({ to: "/wizard" });
  }, [navigate, queryClient]);

  const requestSignIn = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      const result = await requestLibrarySignIn(email);
      setStatus(
        result.sent
          ? t("ui.copy_check_your_email_for_a_one_time_sign_440b1844")
          : t("ui.copy_email_sending_is_not_configured_use_671fd275"),
      );
      setDevMagicLink(result.devMagicLink || "");
    } catch (signInError) {
      setError(
        signInError instanceof Error
          ? signInError.message
          : t("ui.copy_could_not_send_a_sign_in_link_7b72771f"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const clearInvitationAndOpenFriends = () => {
    clearPendingFriendInvite();
    queryClient.removeQueries({ queryKey: appQueryKeys.pendingChartClaim() });
    void navigate({ to: "/friends" });
  };

  return (
    <main className="min-h-screen bg-[#FAF0E6] px-5 pb-16 pt-6 sm:px-10">
      <header className="flex items-center justify-between border-b border-black pb-5">
        <Link to="/" className="block w-fit" aria-label={t("accessibility.harnessHome")}>
          <HarnessWordmark className="block w-[clamp(6.25rem,9vw,8.25rem)]" />
        </Link>
      </header>
      <section className="mx-auto flex max-w-xl flex-col pb-12 pt-14 text-center sm:pt-16">
        {isChartClaimHeading ? (
          <>
            <h1 className="sr-only">
              {t("ui.copy_value_has_invited_you_to_see_your_hu_d81eafae", {
                senderName: senderName,
              })}
            </h1>
            <div className="relative mx-auto w-full max-w-[42rem]">
              <ChartreuseAsteriskCluster className="-top-5 left-[1%] -rotate-12 sm:-top-7" />
              <ChartreuseAsteriskCluster className="-bottom-4 right-[1%] rotate-12 sm:-bottom-6" />
              <svg
                viewBox="0 0 760 190"
                className="w-full overflow-visible font-display text-black"
                aria-hidden="true"
              >
                <defs>
                  <path id="invite-heading-top-curve" d="M 110 72 Q 380 35 650 72" />
                  <path id="invite-heading-bottom-curve" d="M 0 148 Q 380 98 760 148" />
                </defs>
                <text fontSize="53">
                  <textPath href="#invite-heading-top-curve" startOffset="50%" textAnchor="middle">
                    {t("ui.copy_value_has_invited_you_d180fc87", { senderName: senderName })}
                  </textPath>
                </text>
                <text fontSize="53">
                  <textPath
                    href="#invite-heading-bottom-curve"
                    startOffset="50%"
                    textAnchor="middle"
                    textLength="700"
                    lengthAdjust="spacingAndGlyphs"
                  >
                    {t("ui.copy_to_see_your_human_design_reading_7ddc7404")}
                  </textPath>
                </text>
              </svg>
            </div>
          </>
        ) : (
          <h1 className="font-display text-5xl lowercase leading-[0.95] text-black sm:text-7xl">
            {isAccepted
              ? `You are connected with ${senderName}`
              : `${senderName} invited you to connect on Harness`}
          </h1>
        )}
        <p className="mt-6 text-base leading-relaxed text-black/65">
          {isAccepted
            ? "Your invitation was accepted successfully."
            : inviteKind === "connection"
              ? getChartLibrarySession() && ownerChartStatus === "missing"
                ? "Create your primary chart before accepting this invitation."
                : getChartLibrarySession()
                  ? "Accept the invitation to connect."
                  : "Sign in or create your account to accept the invitation."
              : getChartLibrarySession()
                ? "Review the chart details below, then accept it as yours."
                : "Sign in or create your account to review this chart before accepting it as yours."}
        </p>
        {getChartLibrarySession()?.email ? (
          <p className="mt-4 text-sm text-black/55">
            {t("ui.copy_accepting_this_invitation_as_797ca283")}{" "}
            <span className="font-medium text-black">{getChartLibrarySession()?.email}</span>
          </p>
        ) : null}
        {!isAccepted && claimPreview ? (
          <div className="mt-8 rounded-3xl border-2 border-black bg-white/60 p-5 text-left">
            <p className="text-xs uppercase tracking-[0.08em] text-black/55">
              {t("ui.copy_chart_information_59dee6f0")}
            </p>
            <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-black/50">Name</dt>
                <dd>{claimPreview.chartName}</dd>
              </div>
              <div>
                <dt className="text-black/50">{t("birthData.birthDate")}</dt>
                <dd>{claimPreview.birthDate}</dd>
              </div>
              <div>
                <dt className="text-black/50">{t("birthData.birthTime")}</dt>
                <dd>{claimPreview.birthTime}</dd>
              </div>
              <div>
                <dt className="text-black/50">{t("ui.copy_birth_place_fa76a72f")}</dt>
                <dd>{claimPreview.location}</dd>
              </div>
            </dl>
            {hasExistingOwnerChart ? (
              <p className="mt-4 text-sm leading-relaxed text-black/65">
                {t("ui.copy_your_current_chartvalue_stays_as_you_c66b982e", {
                  existingOwnerChartName: existingOwnerChartName
                    ? `, ${existingOwnerChartName},`
                    : "",
                })}
              </p>
            ) : null}
            {getChartLibrarySession() ? (
              hasExistingOwnerChart ? (
                <div className="mt-5 grid gap-3 text-left">
                  <button
                    type="button"
                    onClick={reviewChart}
                    disabled={isLoading}
                    className="min-h-12 w-full rounded-full bg-black px-6 text-sm uppercase tracking-[.08em] text-white disabled:opacity-50"
                  >
                    {t("ui.copy_review_and_replace_my_chart_4a7f4263")}
                  </button>
                  <button
                    type="button"
                    onClick={() => void acceptWithoutClaimingChart()}
                    disabled={isLoading}
                    className="min-h-12 w-full rounded-full border-2 border-black px-6 text-sm uppercase tracking-[.08em] disabled:opacity-50"
                  >
                    {t("ui.copy_accept_invite_keep_my_chart_54ec8644")}
                  </button>
                  <button
                    type="button"
                    onClick={() => void saveInvitedChartAsShared()}
                    disabled={isLoading}
                    className="min-h-12 w-full rounded-full border-2 border-black px-6 text-sm uppercase tracking-[.08em] disabled:opacity-50"
                  >
                    {t("ui.copy_accept_and_save_as_shared_chart_180270bd")}
                  </button>
                  <button
                    type="button"
                    onClick={signOutToAcceptAsAnotherUser}
                    disabled={isLoading}
                    className="min-h-10 w-full text-xs uppercase tracking-[.08em] underline underline-offset-4 disabled:opacity-50"
                  >
                    {t("ui.copy_sign_out_to_accept_as_another_user_cc43ae03")}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={reviewChart}
                  className="mt-5 min-h-12 w-full rounded-full bg-black px-6 text-sm uppercase tracking-[.08em] text-white"
                >
                  {t("ui.copy_review_and_accept_chart_05165474")}
                </button>
              )
            ) : null}
          </div>
        ) : null}
        {!isAccepted &&
        inviteKind === "connection" &&
        getChartLibrarySession() &&
        ownerChartStatus === "missing" ? (
          <button
            type="button"
            onClick={createPrimaryChart}
            disabled={isLoading}
            className="mt-8 min-h-12 w-full rounded-full bg-[#95A789] px-6 text-sm uppercase tracking-[.08em] text-black disabled:opacity-50"
          >
            {t("ui.copy_create_my_primary_chart_8fea357f")}
          </button>
        ) : null}
        {!isAccepted &&
        inviteKind === "connection" &&
        getChartLibrarySession() &&
        ownerChartStatus !== "missing" ? (
          <button
            type="button"
            onClick={() => void acceptConnection()}
            disabled={isLoading}
            className="mt-8 min-h-12 w-full rounded-full bg-[#95A789] px-6 text-sm uppercase tracking-[.08em] text-black disabled:opacity-50"
          >
            {isLoading ? "Accepting…" : "Accept invitation"}
          </button>
        ) : null}
        {!isAccepted && !getChartLibrarySession() ? (
          <div className="mt-8 w-full text-left">
            {googleClientId ? (
              <div ref={googleButtonRef} className="flex h-12 w-full items-center justify-center" />
            ) : null}
            {!showEmail ? (
              <button
                type="button"
                onClick={() => setShowEmail(true)}
                className="mt-3 min-h-12 w-full rounded-full border-2 border-black px-6 text-sm uppercase tracking-[.08em]"
              >
                {t("common.continueWithEmail")}
              </button>
            ) : (
              <form onSubmit={requestSignIn} className="mt-4 space-y-3">
                <input
                  required
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder={t("common.emailPlaceholder")}
                  className="min-h-12 w-full rounded-full border-2 border-black bg-white px-5"
                />
                <button
                  type="submit"
                  disabled={isLoading}
                  className="min-h-12 w-full rounded-full border-2 border-black px-6 text-sm uppercase tracking-[.08em]"
                >
                  {isLoading ? "Sending…" : "Send me a sign-in link"}
                </button>
              </form>
            )}
          </div>
        ) : null}
        {isAccepted ? (
          <Link
            to="/friends"
            className="mt-8 inline-flex min-h-12 items-center justify-center rounded-full bg-black px-6 text-sm uppercase tracking-[.08em] text-white"
          >
            {t("common.openFriends")}
          </Link>
        ) : null}
        {status ? <p className="mt-5 whitespace-pre-line text-sm text-black/65">{status}</p> : null}
        {devMagicLink ? (
          <a href={devMagicLink} className="mt-3 text-sm text-black underline underline-offset-4">
            {t("common.openDevelopmentSignInLink")}
          </a>
        ) : null}
        {error ? (
          <div
            className="fixed inset-x-4 top-4 z-50 mx-auto max-w-lg rounded-2xl border-2 border-black bg-[#FFF9F4] p-4 text-left shadow-[5px_5px_0_#95A789]"
            role="alert"
          >
            <div className="flex items-start gap-3">
              <p className="min-w-0 flex-1 text-sm leading-relaxed text-red-900">{error}</p>
              <button
                type="button"
                aria-label={t("ui.copy_dismiss_notification_4e0041d1")}
                onClick={() => setError("")}
                className="-mr-1 -mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-2xl leading-none text-black/65 transition hover:bg-black/10 hover:text-black"
              >
                ×
              </button>
            </div>
            {!isAccepted && token ? (
              <button
                type="button"
                onClick={clearInvitationAndOpenFriends}
                className="mt-3 text-xs uppercase tracking-[.08em] underline underline-offset-4"
              >
                {t("ui.copy_clear_invitation_and_open_friends_2054d4c0")}
              </button>
            ) : null}
          </div>
        ) : null}
      </section>
    </main>
  );
}

function firstName(value: string) {
  const name = value.trim();
  return name ? name.split(/\s+/)[0] : "A friend";
}
